import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import React, { useState, useRef, useCallback, useMemo, useEffect, } from "react";
import { CaretDownIcon, AlertIcon } from "../Icons/Icons";
import { getOptionKey } from "../../utils/utils";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
const SingleSelect = React.forwardRef(({ label, name, items, onSelectionChange, isInvalid = false, isDisabled = false, errorMessage, selectedKey, selectLabel, readOnly, className = "", isLastElement = false, isEvenItems = true, ...props }, forwardedRef) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const listRef = useRef(null);
    const optionRefs = useRef([]);
    const { t } = useLocalizedString();
    const selectedOption = useMemo(() => items.find((item) => item.value === selectedKey), [items, selectedKey]);
    useEffect(() => {
        if (!buttonRef.current)
            return;
        if (typeof forwardedRef === "function") {
            forwardedRef(buttonRef.current);
        }
        else if (forwardedRef) {
            forwardedRef.current = buttonRef.current;
        }
    }, [forwardedRef]);
    useEffect(() => {
        if (isOpen &&
            focusedIndex >= 0 &&
            focusedIndex < optionRefs.current.length) {
            optionRefs.current[focusedIndex]?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [focusedIndex, isOpen]);
    const handleMainInputClick = useCallback((e) => {
        e.preventDefault();
        if (!isDisabled) {
            setIsOpen((prev) => !prev);
        }
    }, [isDisabled]);
    const handleOptionClick = useCallback((option) => {
        onSelectionChange(option.value);
        setIsOpen(false);
        setFocusedIndex(-1);
    }, [onSelectionChange]);
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case "Enter":
                e.preventDefault();
                if (isDisabled)
                    return;
                if (!isOpen) {
                    setIsOpen(true);
                }
                else if (focusedIndex >= 0) {
                    handleOptionClick(items[focusedIndex]);
                }
                break;
            case "ArrowDown":
                e.preventDefault();
                if (isDisabled)
                    return;
                if (!isOpen) {
                    setIsOpen(true);
                }
                else {
                    setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
                }
                break;
            case "ArrowUp":
                e.preventDefault();
                if (isOpen) {
                    setFocusedIndex((prev) => Math.max(prev - 1, -1));
                }
                break;
            case "Escape":
                setIsOpen(false);
                setFocusedIndex(-1);
                break;
        }
    }, [isDisabled, isOpen, focusedIndex, items, handleOptionClick]);
    useEffect(() => {
        const handleClick = (event) => {
            if (buttonRef.current?.contains(event.target)) {
                return;
            }
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);
    const baseClassNames = {
        container: `relative w-full ${className}`,
        wrapper: `relative w-full border rounded-lg text-left h-[52px] ${isInvalid && errorMessage ? "border-red-600" : "border-gray-300"} ${isDisabled ? "bg-gray-100" : ""}`,
        label: "absolute top-[3px] left-3 text-sm text-gray-600 pointer-events-none",
        button: `
      flex justify-between items-end w-full h-full px-3 pt-5 pb-1 text-base bg-transparent 
      border-0 rounded-lg cursor-pointer focus:ring-1   focus:ring-blue-500 focus:shadow-idk-box-shadow
      outline-none 
      ${isDisabled ? "opacity-60 pointer-events-none bg-gray-100" : ""}
      ${isOpen ? "shadow-[2px_2px_8px_0_#F299FF,-2px_-2px_8px_0_#FEEB3E]" : ""}
    ${!isOpen && !isDisabled ? "hover:idk-box-shadow" : ""}
      transition-all duration-300 ease-in-out
    `,
        dropdown: `
        absolute left-0 right-0 z-[2000] flex flex-col max-h-[calc(100vh-60px)] 
        overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg 
        top-[57px] p-2
      `,
        itemsList: "max-h-[200px] overflow-y-auto list-none p-0 m-0",
        item: (isSelected, isFocused) => `
        rounded-lg px-3 py-3 text-base font-medium flex justify-between cursor-pointer
        ${isSelected || isFocused ? "bg-idk-gradient" : ""}
      `,
        error: "flex items-center gap-1 mt-2.5 text-red-600 text-sm font-medium",
    };
    if (readOnly) {
        return (_jsxs("div", { className: clsx("flex flex-col items-start gap-1 px-0 py-4 sm:p-4 h-auto min-h-[8.4rem] overflow-hidden", "border-b border-[#ccccd1]", "[&:last-child]:border-b-0", isEvenItems && "md:[&:nth-last-child(2)]:border-b-0", className), children: [_jsx("div", { className: "text-sm text-idk-disabled-text", children: label }), _jsx("div", { className: "text-sm", children: t(getOptionKey(items, selectedOption?.value || "")) })] }));
    }
    return (_jsx("div", { onKeyDown: handleKeyDown, role: "combobox", tabIndex: -1, "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-controls": "items-list", "aria-owns": "items-list", className: baseClassNames.container, children: _jsxs("div", { children: [_jsxs("div", { className: baseClassNames.wrapper, children: [_jsx("div", { className: baseClassNames.label, children: label }), _jsxs("button", { ...props, ref: buttonRef, onClick: handleMainInputClick, "aria-controls": "items-list", className: baseClassNames.button, "aria-label": name, disabled: isDisabled, type: "button", children: [_jsx("span", { className: "overflow-hidden overflow-ellipsis whitespace-nowrap", children: t(selectedOption?.key || selectLabel || label, selectedOption?.key || selectLabel || label) }), _jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: _jsx("span", { className: clsx("transform transition-transform duration-200 block", isOpen ? "-rotate-180" : ""), "aria-hidden": "true", children: _jsx(CaretDownIcon, {}) }) })] }), isOpen && (_jsx("div", { ref: dropdownRef, className: baseClassNames.dropdown, children: _jsx("ul", { id: "items-list", ref: listRef, role: "listbox", className: baseClassNames.itemsList, children: items.map((option, index) => (_jsx("li", { ref: (el) => (optionRefs.current[index] = el), role: "option", tabIndex: 0, "aria-selected": option === selectedOption, onClick: () => handleOptionClick(option), onMouseEnter: () => setFocusedIndex(index), className: baseClassNames.item(option === selectedOption, index === focusedIndex), children: t(option.key, option.key) }, option.value))) }) }))] }), isInvalid && errorMessage && (_jsxs("div", { className: baseClassNames.error, children: [_jsx(AlertIcon, {}), _jsx("span", { children: errorMessage })] }))] }) }));
});
SingleSelect.displayName = "SingleSelect";
export default SingleSelect;
//# sourceMappingURL=SingleSelect.js.map